/**
 * ADF (Atlassian Document Format) → React renderer.
 *
 * Renders Jira rich text content including paragraphs, headings,
 * bold/italic/code marks, links, images, lists, tables, code blocks, etc.
 */

import { useMemo, useState, type ReactNode } from 'react';
import type { JiraAttachment } from '@shared/types.js';
import { ImageModal } from '@/components/ImageModal.js';

/**
 * Validate that a URL uses a safe protocol.
 * Rejects javascript:, data:, vbscript:, and other dangerous schemes.
 */
function isSafeHref(url: string | undefined): boolean {
  if (!url) return false;
  const trimmed = url.trim().toLowerCase();
  return trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('mailto:');
}

interface AdfNode {
  type: string;
  text?: string;
  content?: AdfNode[];
  marks?: Array<{ type: string; attrs?: Record<string, string> }>;
  attrs?: Record<string, unknown>;
}

interface AdfDoc {
  type: 'doc';
  version: number;
  content: AdfNode[];
}

interface AdfRendererProps {
  adf: unknown;
  fallbackText?: string | null;
  attachments?: JiraAttachment[];
}

type MediaMap = Map<string, JiraAttachment>;

function hasRenderableMarkdown(text: string): boolean {
  return /(^|\n)(#{1,6}\s|\d+\.\s|[-*]\s|>\s)|(\*\*[^*]+\*\*)|(`[^`]+`)|(\[[^\]]+\]\((https?:\/\/|mailto:)[^)]+\))/.test(text);
}

function extractPlainText(node: AdfNode | undefined): string {
  if (!node) return '';
  if (node.type === 'text') return node.text || '';
  if (node.type === 'hardBreak') return '\n';
  if (!node.content) return '';
  return node.content.map(extractPlainText).join(node.type === 'doc' ? '\n\n' : '');
}

function isPlainTextAdf(node: AdfNode | AdfDoc | undefined): boolean {
  if (!node || typeof node !== 'object') return false;
  if ('type' in node === false) return false;
  if (node.type === 'doc') return !!node.content?.every(isPlainTextAdf);
  if (node.type === 'paragraph') return !!node.content?.every(isPlainTextAdf);
  if (node.type === 'text') return !node.marks || node.marks.length === 0;
  if (node.type === 'hardBreak') return true;
  return false;
}

function renderInlineMarkdown(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /(\[[^\]]+\]\((https?:\/\/|mailto:)[^)]+\))|(\*\*[^*]+\*\*)|(`[^`]+`)/g;
  let lastIndex = 0;
  let key = 0;

  for (const match of text.matchAll(pattern)) {
    const [token] = match;
    const index = match.index ?? 0;
    if (index > lastIndex) nodes.push(text.slice(lastIndex, index));

    if (token.startsWith('[')) {
      const linkMatch = token.match(/^\[([^\]]+)\]\(((https?:\/\/|mailto:)[^)]+)\)$/);
      if (linkMatch && isSafeHref(linkMatch[2])) {
        nodes.push(
          <a
            key={`link-${key++}`}
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {linkMatch[1]}
          </a>,
        );
      } else {
        nodes.push(token);
      }
    } else if (token.startsWith('**') && token.endsWith('**')) {
      nodes.push(<strong key={`strong-${key++}`}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`') && token.endsWith('`')) {
      nodes.push(<code key={`code-${key++}`} className="rounded bg-gray-100 px-1 py-0.5 text-xs font-mono text-red-600">{token.slice(1, -1)}</code>);
    } else {
      nodes.push(token);
    }

    lastIndex = index + token.length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

function renderLineWithBreaks(text: string): ReactNode[] {
  return text.split('\n').flatMap((segment, idx, parts) => {
    const nodes: ReactNode[] = renderInlineMarkdown(segment);
    if (idx < parts.length - 1) nodes.push(<br key={`br-${idx}`} />);
    return nodes;
  });
}

function renderPlainTextMarkdown(text: string): ReactNode {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    if (!lines[i].trim()) {
      i += 1;
      continue;
    }

    const headingMatch = lines[i].match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const cls: Record<number, string> = {
        1: 'text-xl',
        2: 'text-lg',
        3: 'text-base',
        4: 'text-sm',
        5: 'text-sm',
        6: 'text-xs',
      };
      const content = renderInlineMarkdown(headingMatch[2]);
      blocks.push(level === 1
        ? <h1 key={`h-${i}`} className={`${cls[level]} mb-2 font-semibold`}>{content}</h1>
        : level === 2
          ? <h2 key={`h-${i}`} className={`${cls[level]} mb-2 font-semibold`}>{content}</h2>
          : level === 3
            ? <h3 key={`h-${i}`} className={`${cls[level]} mb-2 font-semibold`}>{content}</h3>
            : level === 4
              ? <h4 key={`h-${i}`} className={`${cls[level]} mb-2 font-semibold`}>{content}</h4>
              : level === 5
                ? <h5 key={`h-${i}`} className={`${cls[level]} mb-2 font-semibold`}>{content}</h5>
                : <h6 key={`h-${i}`} className={`${cls[level]} mb-2 font-semibold`}>{content}</h6>);
      i += 1;
      continue;
    }

    if (/^[-*]\s+/.test(lines[i])) {
      const items: ReactNode[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(<li key={`ul-${i}`}>{renderInlineMarkdown(lines[i].replace(/^[-*]\s+/, ''))}</li>);
        i += 1;
      }
      blocks.push(<ul key={`ul-block-${i}`} className="mb-2 ml-5 list-disc space-y-1">{items}</ul>);
      continue;
    }

    if (/^\d+\.\s+/.test(lines[i])) {
      const items: ReactNode[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(<li key={`ol-${i}`}>{renderInlineMarkdown(lines[i].replace(/^\d+\.\s+/, ''))}</li>);
        i += 1;
      }
      blocks.push(<ol key={`ol-block-${i}`} className="mb-2 ml-5 list-decimal space-y-1">{items}</ol>);
      continue;
    }

    if (/^>\s+/.test(lines[i])) {
      const quoteLines: string[] = [];
      while (i < lines.length && /^>\s+/.test(lines[i])) {
        quoteLines.push(lines[i].replace(/^>\s+/, ''));
        i += 1;
      }
      blocks.push(
        <blockquote key={`quote-${i}`} className="mb-2 border-l-4 border-gray-300 pl-3 italic text-gray-600">
          <p>{renderLineWithBreaks(quoteLines.join('\n'))}</p>
        </blockquote>,
      );
      continue;
    }

    const paragraphLines: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,6}\s|[-*]\s+|\d+\.\s+|>\s+)/.test(lines[i])) {
      paragraphLines.push(lines[i]);
      i += 1;
    }
    blocks.push(
      <p key={`p-${i}`} className="mb-2 last:mb-0">
        {renderLineWithBreaks(paragraphLines.join('\n'))}
      </p>,
    );
  }

  return <div className="text-sm text-gray-700">{blocks}</div>;
}

function ClickableImage({ src, alt, className }: { src: string; alt: string; className: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <img
        src={src}
        alt={alt}
        className={`${className} cursor-pointer`}
        loading="lazy"
        onClick={() => setOpen(true)}
      />
      {open && <ImageModal src={src} alt={alt} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * Embedded media: images open in the lightbox; other files (PDFs, logs, ...)
 * render as a download chip. Resolves the media UUID (or filename) to the
 * issue attachment that holds it.
 */
function renderMedia(node: AdfNode, idx: number, mm: MediaMap, imgClass: string): ReactNode {
  const attrs = node.attrs ?? {};
  if (attrs.type === 'external') {
    const url = attrs.url as string | undefined;
    return isSafeHref(url) ? <ClickableImage key={idx} src={url!} alt={(attrs.alt as string) || ''} className={imgClass} /> : null;
  }
  const mid = attrs.id as string | undefined;
  const alt = (attrs.alt as string) || '';
  if (!mid) return null;
  const attachment = mm.get(mid) ?? (alt ? mm.get(alt) : undefined);
  const src = `/api/attachments/${attachment ? attachment.id : mid}`;
  if (attachment?.mimeType && !attachment.mimeType.startsWith('image/')) {
    return (
      <a key={idx} href={src} target="_blank" rel="noopener noreferrer" className="my-1 mr-2 inline-flex items-center gap-2 rounded border bg-gray-50 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100">
        <svg className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden><path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 1 0 2.828 2.828l6.414-6.586a4 4 0 0 0-5.656-5.656l-6.415 6.585a6 6 0 1 0 8.486 8.486L20.5 13" /></svg>
        <span className="max-w-[240px] truncate">{attachment.filename}</span>
      </a>
    );
  }
  return <ClickableImage key={idx} src={src} alt={alt || attachment?.filename || ''} className={imgClass} />;
}

function applyMarks(text: string, marks?: AdfNode['marks']): ReactNode {
  if (!marks || marks.length === 0) return text;
  let node: ReactNode = text;
  for (const mark of marks) {
    switch (mark.type) {
      case 'strong': node = <strong>{node}</strong>; break;
      case 'em': node = <em>{node}</em>; break;
      case 'code': node = <code className="rounded bg-gray-100 px-1 py-0.5 text-xs font-mono text-red-600">{node}</code>; break;
      case 'strike': node = <s>{node}</s>; break;
      case 'underline': node = <u>{node}</u>; break;
      case 'link':
        if (isSafeHref(mark.attrs?.href)) {
          node = <a href={mark.attrs?.href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{node}</a>;
        }
        break;
      case 'textColor': node = <span style={{ color: mark.attrs?.color }}>{node}</span>; break;
      case 'subsup': node = mark.attrs?.type === 'sub' ? <sub>{node}</sub> : <sup>{node}</sup>; break;
    }
  }
  return node;
}

function renderInline(node: AdfNode, idx: number, mm: MediaMap): ReactNode {
  switch (node.type) {
    case 'text': {
      // Portal mention of a teammate without Jira: "@Name" linked to mailto:<email>
      const portalMention = node.text?.startsWith('@') && node.marks?.length === 1 && node.marks[0]!.type === 'link'
        && /^mailto:/i.test(node.marks[0]!.attrs?.href ?? '');
      if (portalMention) return <span key={idx} className="rounded bg-green-50 px-1 text-green-700" title={node.marks![0]!.attrs!.href!.slice(7)}>{node.text}</span>;
      return <span key={idx}>{applyMarks(node.text || '', node.marks)}</span>;
    }
    case 'hardBreak':
      return <br key={idx} />;
    case 'emoji':
      return <span key={idx}>{(node.attrs?.text as string) || (node.attrs?.shortName as string) || ''}</span>;
    case 'mention':
      // Jira stores mention text with its own leading "@" ("@Bill Vezzoso").
      return <span key={idx} className="rounded bg-blue-50 px-1 text-blue-700">@{((node.attrs?.text as string) || 'user').replace(/^@+/, '') || 'user'}</span>;
    case 'inlineCard': {
      const cardUrl = node.attrs?.url as string;
      if (isSafeHref(cardUrl)) {
        return <a key={idx} href={cardUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{cardUrl || 'Link'}</a>;
      }
      return <span key={idx}>{cardUrl || 'Link'}</span>;
    }
    case 'mediaInline':
    case 'media':
      return renderMedia(node, idx, mm, 'my-2 inline-block max-w-full rounded');
    case 'status': {
      const colors: Record<string, string> = { neutral: 'bg-gray-200 text-gray-700', purple: 'bg-purple-100 text-purple-700', blue: 'bg-blue-100 text-blue-700', red: 'bg-red-100 text-red-700', yellow: 'bg-yellow-100 text-yellow-800', green: 'bg-green-100 text-green-700' };
      return <span key={idx} className={`rounded px-1 text-[11px] font-bold uppercase ${colors[(node.attrs?.color as string) || 'neutral'] || colors.neutral}`}>{(node.attrs?.text as string) || ''}</span>;
    }
    case 'date': {
      const ts = Number(node.attrs?.timestamp);
      return <span key={idx} className="rounded bg-gray-100 px-1">{Number.isFinite(ts) ? new Date(ts).toLocaleDateString(undefined, { timeZone: 'UTC' }) : ''}</span>;
    }
    default:
      if (node.content) return <span key={idx}>{node.content.map((n, i) => renderInline(n, i, mm))}</span>;
      return node.text ? <span key={idx}>{node.text}</span> : null;
  }
}

function renderBlock(node: AdfNode, idx: number, mm: MediaMap): ReactNode {
  switch (node.type) {
    case 'paragraph':
      return <p key={idx} className="mb-2 last:mb-0">{node.content?.map((n, i) => renderInline(n, i, mm))}</p>;

    case 'heading': {
      const level = (node.attrs?.level as number) || 1;
      const sizes: Record<number, string> = { 1: 'text-xl', 2: 'text-lg', 3: 'text-base', 4: 'text-sm', 5: 'text-sm', 6: 'text-xs' };
      const cls = `${sizes[level] || 'text-base'} mb-2 font-semibold`;
      const children = node.content?.map((n, i) => renderInline(n, i, mm));
      if (level === 1) return <h1 key={idx} className={cls}>{children}</h1>;
      if (level === 2) return <h2 key={idx} className={cls}>{children}</h2>;
      if (level === 3) return <h3 key={idx} className={cls}>{children}</h3>;
      if (level === 4) return <h4 key={idx} className={cls}>{children}</h4>;
      if (level === 5) return <h5 key={idx} className={cls}>{children}</h5>;
      return <h6 key={idx} className={cls}>{children}</h6>;
    }

    case 'bulletList':
      return <ul key={idx} className="mb-2 ml-5 list-disc space-y-1">{node.content?.map((n, i) => renderBlock(n, i, mm))}</ul>;
    case 'orderedList':
      return <ol key={idx} className="mb-2 ml-5 list-decimal space-y-1">{node.content?.map((n, i) => renderBlock(n, i, mm))}</ol>;
    case 'listItem':
      return <li key={idx}>{node.content?.map((n, i) => renderBlock(n, i, mm))}</li>;

    case 'blockquote':
      return <blockquote key={idx} className="mb-2 border-l-4 border-gray-300 pl-3 italic text-gray-600">{node.content?.map((n, i) => renderBlock(n, i, mm))}</blockquote>;

    case 'taskList':
    case 'decisionList':
      return (
        <ul key={idx} className="mb-2 space-y-1">
          {node.content?.map((n, i) => (n.type === 'taskList' ? <li key={i} className="ml-5">{renderBlock(n, i, mm)}</li> : renderBlock(n, i, mm)))}
        </ul>
      );
    case 'taskItem':
    case 'decisionItem': {
      const done = node.attrs?.state === 'DONE' || node.attrs?.state === 'DECIDED';
      // Real controls/shapes rather than ☐/◆ glyphs, which some fonts lack
      const marker = node.type === 'decisionItem'
        ? <span className="mt-1.5 h-2 w-2 shrink-0 rotate-45 bg-gray-500" />
        : <input type="checkbox" checked={done} readOnly disabled className="mt-1 h-3.5 w-3.5 shrink-0 accent-blue-600" />;
      // ADF task items hold inline content; nested task lists are their siblings
      return (
        <li key={idx} className={`flex gap-2 ${node.type === 'taskItem' && done ? 'text-gray-400 line-through' : ''}`}>
          <span aria-hidden className="flex select-none">{marker}</span>
          <span>{node.content?.map((n, i) => renderInline(n, i, mm))}</span>
        </li>
      );
    }

    case 'codeBlock':
      return <pre key={idx} className="mb-2 overflow-x-auto rounded bg-gray-100 p-3 text-xs font-mono"><code>{node.content?.map((c) => c.text || '').join('')}</code></pre>;

    case 'rule':
      return <hr key={idx} className="my-3 border-gray-200" />;

    case 'mediaSingle':
    case 'mediaGroup':
      return <div key={idx} className="my-2">{node.content?.map((n, i) => renderMediaNode(n, i, mm))}</div>;

    case 'table':
      return <table key={idx} className="mb-2 w-full border-collapse text-sm"><tbody>{node.content?.map((n, i) => renderBlock(n, i, mm))}</tbody></table>;
    case 'tableRow':
      return <tr key={idx}>{node.content?.map((n, i) => renderBlock(n, i, mm))}</tr>;
    case 'tableHeader':
      return <th key={idx} className="border border-gray-200 bg-gray-50 p-2 text-left font-semibold">{node.content?.map((n, i) => renderBlock(n, i, mm))}</th>;
    case 'tableCell':
      return <td key={idx} className="border border-gray-200 p-2">{node.content?.map((n, i) => renderBlock(n, i, mm))}</td>;

    case 'panel': {
      const pt = (node.attrs?.panelType as string) || 'info';
      const colors: Record<string, string> = { info: 'bg-blue-50 border-blue-200', note: 'bg-purple-50 border-purple-200', warning: 'bg-yellow-50 border-yellow-200', success: 'bg-green-50 border-green-200', error: 'bg-red-50 border-red-200' };
      return <div key={idx} className={`mb-2 rounded border p-3 ${colors[pt] || colors.info}`}>{node.content?.map((n, i) => renderBlock(n, i, mm))}</div>;
    }

    case 'expand':
    case 'nestedExpand':
      return (
        <details key={idx} className="mb-2">
          <summary className="cursor-pointer font-medium text-gray-700">{(node.attrs?.title as string) || 'Expand'}</summary>
          <div className="mt-1 pl-2">{node.content?.map((n, i) => renderBlock(n, i, mm))}</div>
        </details>
      );

    default:
      if (node.content) return <div key={idx}>{node.content.map((n, i) => renderBlock(n, i, mm))}</div>;
      return null;
  }
}

function renderMediaNode(node: AdfNode, idx: number, mm: MediaMap): ReactNode {
  if (node.type === 'media') return renderMedia(node, idx, mm, 'max-w-full rounded');
  if (node.content) return <div key={idx}>{node.content.map((n, i) => renderBlock(n, i, mm))}</div>;
  return null;
}

export function AdfRenderer({ adf, fallbackText, attachments }: AdfRendererProps) {
  // Build a map from media file UUID → attachment ID
  const mediaMap = useMemo(() => {
    const map: MediaMap = new Map();
    if (attachments) {
      for (const a of attachments) {
        // Map by media API file ID (UUID)
        if (a.mediaApiFileId) map.set(a.mediaApiFileId, a);
        // Also map by filename — Jira sometimes references images by name
        if (a.filename) map.set(a.filename, a);
      }
    }
    return map;
  }, [attachments]);

  if (!adf || typeof adf !== 'object') {
    if (fallbackText) return <div className="whitespace-pre-wrap text-sm text-gray-700">{fallbackText}</div>;
    return null;
  }

  const doc = adf as AdfDoc;
  if (doc.type !== 'doc' || !doc.content) {
    if (fallbackText) return <div className="whitespace-pre-wrap text-sm text-gray-700">{fallbackText}</div>;
    return null;
  }

  const plainText = extractPlainText(doc);
  if (isPlainTextAdf(doc) && plainText.trim()) {
    return hasRenderableMarkdown(plainText)
      ? renderPlainTextMarkdown(plainText)
      : <div className="whitespace-pre-wrap text-sm text-gray-700">{plainText}</div>;
  }

  return (
    <div className="adf-content text-sm text-gray-700">
      {doc.content.map((n, i) => renderBlock(n, i, mediaMap))}
    </div>
  );
}
